<template>
  <rui-card-border :class="`fire-card ${vehicle.arrived ? 'fire-card--highlight' : ''}`"
                   bodyMaxHeight="calc(var(--rui-card-head-height) * 3 + var(--rui-space-base) * 2)">
    <template v-slot:header>
      <div class="fire-card__badge">Vehicle PAR&nbsp;&ndash;&nbsp;{{ vehicle.par }}</div>
      <div class="fire-card__badge--left"
           v-if="vehicle.arrived">
        {{ newVehicle }}
      </div>
      <div class="fire-toolbar">
        <h3>
          <div class="fire-vehicle-name">{{ vehicle.name }}</div>
          <fire-assignment-label :text="vehicle.assignment"/>
        </h3>
        <rui-notify :text="vehicle.team.length">
          <template v-slot:body>
            <rui-icon icon="group"/>
          </template>
        </rui-notify>
      </div>
      <slot name="openModalButton"/>
    </template>
    <template v-slot:body
              v-if="expanded">
      <ul class="fire-list">
        <li v-for="(member, index) in vehicle.team"
            v-bind:key="index">
          <div class="fire-name">
            <div>
              {{ index + 1 }}.&nbsp;&nbsp;
              {{ checkRank(member.rank) }}{{ member.fullName }}
            </div>
            <div class="fire-location">{{ member.location }}</div>
          </div>

          <rui-checkbox type="checkbox"
                        v-if="checkboxVisible()"
                        class="fire-checkbox"
                        :disabled="checkboxDisabled()"
                        @changeCheckbox="handleChange($event.checked)"/>
          <div v-if="rankIconVisible()"
               :class="`fire-level${checkRankIcon(member.rank)}`"></div>
        </li>
      </ul>
    </template>
  </rui-card-border>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

import RuiCardBorder from '@/retinus-ui/components/card-border/RuiCardBorder.vue';
import RuiIcon from '@/retinus-ui/components/icons/RuiIcon.vue';
import RuiCheckbox from '@/retinus-ui/components/checkbox/RuiCheckbox.vue';
import RuiNotify from '@/retinus-ui/components/notify/RuiNotify.vue';
import FireAssignmentLabel from '@/app/views/on-scene/components/assignment-label/FireAssignmentLabel.vue';

import OnSceneTools from '@/app/views/on-scene/OnSceneTools';
import {ParState} from '@/app/core/interfaces';
import {NEW_VEHICLE} from '@/app/core/constants';
import {mapActions} from 'vuex';
import {VehiclesModuleKey} from '@/app/data-flow/vehicles';

export default defineComponent({
  name: 'fire-vehicle-card',
  components: {
    RuiCardBorder,
    RuiIcon,
    RuiCheckbox,
    RuiNotify,
    FireAssignmentLabel,
  },
  emits: ['parComplete'],
  props: {
    vehicle: {
      type: Object,
      required: true,
    },
    activeCardState: Boolean,
    expanded: {
      type: Boolean,
      required: true,
    },
    startPar: String,
  },
  data() {
    return {
      newVehicle: NEW_VEHICLE,
      modelItem: null,
      modalVisible: false as boolean,
      checkedTeam: 0 as number,
      parFinish: false as boolean,
      startParProcess: undefined as ParState,
    };
  },
  methods: {
    ...mapActions(VehiclesModuleKey, [
      'updateVehiclePar',
    ]),

    checkRank(rank: string) {
      return OnSceneTools.checkRank(rank);
    },

    checkRankIcon(rank: string) {
      return OnSceneTools.checkRankIcon(rank);
    },

    checkboxVisible(): boolean {
      const parProcess = this.startParProcess === 'START';
      return parProcess && !this.parFinish;
    },

    checkboxDisabled(): boolean {
      return !this.activeCardState;
    },

    rankIconVisible(): boolean {
      return !this.startParProcess || (this.startParProcess && this.parFinish);
    },

    handleChange(checked: boolean) {
      // The number of PARs for the vehicle card header.
      if (checked) {
        this.checkedTeam += 1;
      } else {
        this.checkedTeam -= 1;
      }

      // Checked All members in the current Vehicle.
      if (this.checkedTeam === this.vehicle?.team.length) {
        const changedPar = this.vehicle.par + 1;
        this.updateVehiclePar({
          vehicleId: this.vehicle.vehicleId as string,
          par: changedPar,
        })
          .then(() => {
            this.parFinish = true;
            this.$emit('parComplete');
          });
      }
    },

    watchStartPar() {
      const fromUndefinedToStart = !this.startParProcess && this.startPar;
      const fromPauseToStart = this.startParProcess === 'PAUSE' && this.startPar === 'START';

      if (this.parFinish && fromUndefinedToStart) {
        this.parFinish = false;
      }

      // If a new PAR process is running (definitely not after a pause).
      if (!this.parFinish && (fromUndefinedToStart || fromPauseToStart)) {
        this.checkedTeam = 0;
      }

      this.startParProcess = this.startPar as ParState;
    },
  },
  watch: {
    startPar: 'watchStartPar',
  },
});
</script>

<style lang="scss" scoped>
@import 'card';
@import '../../list';
</style>
